import { useState } from 'react';
import { Link } from 'react-router';
import { useAuth } from '@/auth/auth-context';
import { useI18n } from '@/i18n/i18n-provider';
import { MODULE_ICONS } from '@/modules/dashboard/module-icons';
import type { NavItem } from '@/layouts/nav-sections';
import { EmptyState } from '@/components/ui';
import { SHORTCUT_SOURCE_SECTIONS, resolveNavItem, useShortcuts } from './shortcuts';
import { StarIcon } from './star-icon';

/**
 * Manage personal shortcuts: the pages already starred (from here or from
 * the sidebar's own star button), and every page the caller can reach,
 * searchable, to star more. Nothing here is shared with the rest of the
 * church — see `shortcuts.ts`.
 */
export function ShortcutsPage() {
  const { t } = useI18n();
  const { can } = useAuth();
  const { shortcuts, isShortcut, toggle } = useShortcuts();
  const [search, setSearch] = useState('');

  const starredItems = shortcuts
    .map((to) => resolveNavItem(to))
    .filter((item): item is NavItem => item !== undefined)
    .filter((item) => item.permission === null || can(item.permission));

  const query = search.trim().toLowerCase();
  const browseSections = SHORTCUT_SOURCE_SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter(
      (item) =>
        (item.permission === null || can(item.permission)) &&
        (query === '' || t(item.labelKey).toLowerCase().includes(query)),
    ),
  })).filter((section) => section.items.length > 0);

  return (
    <div className="page shortcuts-page">
      <header className="page-header">
        <div>
          <h1 className="page-title">{t('shortcuts.title')}</h1>
          <p className="page-count">{t('shortcuts.hint')}</p>
        </div>
      </header>

      <section>
        <h2 className="dashboard-section-title">{t('shortcuts.yours')}</h2>

        {starredItems.length === 0 ? (
          <EmptyState title={t('shortcuts.emptyTitle')} hint={t('shortcuts.emptyHint')} />
        ) : (
          <div className="dashboard-tiles">
            {starredItems.map((item) => {
              const Icon = MODULE_ICONS[item.to];
              return (
                <div className="shortcut-tile" key={item.to}>
                  <Link to={item.to} className="dashboard-tile">
                    <span className="dashboard-tile-icon">{Icon && <Icon />}</span>
                    <span className="dashboard-tile-label">{t(item.labelKey)}</span>
                  </Link>
                  <button
                    type="button"
                    className="shortcut-tile-remove"
                    onClick={() => toggle(item.to)}
                    aria-label={t('shortcuts.remove')}
                    title={t('shortcuts.remove')}
                  >
                    ×
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </section>

      <section className="shortcuts-browse">
        <h2 className="dashboard-section-title">{t('shortcuts.addMore')}</h2>

        <label className="shortcuts-search">
          <span className="sr-only">{t('common.actions.search')}</span>
          <input
            type="search"
            className="shell-nav-search-input"
            placeholder={t('common.actions.search')}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            autoComplete="off"
          />
        </label>

        {browseSections.length === 0 ? (
          <p className="page-count">{t('navigation.noMenuMatches')}</p>
        ) : (
          browseSections.map((section) => (
            <div key={section.id} className="shortcuts-browse-section">
              {section.labelKey && (
                <p className="dashboard-category-label">{t(section.labelKey)}</p>
              )}
              <ul className="shortcuts-browse-list">
                {section.items.map((item) => {
                  const Icon = MODULE_ICONS[item.to];
                  const active = isShortcut(item.to);
                  return (
                    <li key={item.to}>
                      <button
                        type="button"
                        className="shortcuts-browse-item"
                        data-active={active || undefined}
                        onClick={() => toggle(item.to)}
                      >
                        <span className="shell-nav-icon">{Icon && <Icon size={18} />}</span>
                        <span className="shortcuts-browse-label">{t(item.labelKey)}</span>
                        <StarIcon filled={active} />
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))
        )}
      </section>
    </div>
  );
}
